import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  LicensingBill,
  LicensingFeePerRateBreakdown,
  LicensingFeePerRateBreakdownFeeEnum,
} from '../../../swagger'
import useBreakdownDataLicensingFee from '../../../hooks/useBreakdownDataLicensingFee'
import {
  BreakDownLedgerVariants,
  BreakdownLedger,
  BreakdownLedgerContainer,
} from './BreakdownLedger'
import TableHeaders from '../../Interfaces/TableHeaders'
import { Box, useTheme } from '@mui/material'
import { convertTwoDecimalStringToBig } from '../../../utils/bigJSHelper'

interface LicensingBreakdownProps {
  licensingBill: LicensingBill
  programName: string
  currencyCode: string
  showDetails: boolean
  isLcd?: boolean
}

/**
 * TODO: Refactor this component to use the useLicensingPaymentBreakdownCalculations hook
 */
export const LicensingPaymentBreakdown: React.FC<LicensingBreakdownProps> = ({
  licensingBill,
  programName,
  currencyCode,
  showDetails,
  isLcd,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isChallenge = programName.includes('Challenge')

  const lcdLabels = {
    licensingFeeApplication: t(
      'License.Table.Breakdown.Header.LicenseFee',
      'Invitation License Fee'
    ),
    firstSemesterLicensingTuition: t(
      'License.Table.Breakdown.Header.FirstSemesterLicenseTuition',
      '1st Semester License Fee'
    ),
    secondSemesterLicensingTuition: t(
      'License.Table.Breakdown.Header.SecondSemesterLicenseTuition',
      '2nd Semester License Fee'
    ),
    licenseFee: t('License.Table.Breakdown.Header.LicenseFee', 'License Fee'),
  }

  const licensingFeeApplicationLabel = t(
    'License.Table.Breakdown.Header.LicenseFee',
    'License Fee Application'
  )
  const licensingTuitionLabel = t(
    'License.Table.Breakdown.Header.LicenseTuition',
    'License Tuition'
  )
  const firstSemesterLicensingTuitionLabel = t(
    'License.Table.Breakdown.Header.FirstSemesterLicenseTuition',
    '1st Semester License Tuition'
  )
  const secondSemesterLicensingTuitionLabel = t(
    'License.Table.Breakdown.Header.SecondSemesterLicenseTuition',
    '2nd Semester License Tuition'
  )

  const licensingBillBreakdown = licensingBill.fees.map(
    (breakdown) => breakdown.breakdown
  )[0]

  const balance = licensingBill.total.amount
  const [{ owed, paid }] = licensingBill.fees.map((fee) => {
    return {
      paid: fee.paid.amount,
      owed: fee.owed.amount,
    }
  })

  const { licensingDue } = licensingBill

  const breakdownInitialValue: LicensingFeePerRateBreakdown = {
    count: 0,
    amount: {
      amount: '0.00',
      currencyCode: '',
    },
  }

  const breakdown = licensingBillBreakdown.reduce(
    (acc, curr) => {
      return {
        semester1FirstStudentsInfo:
          curr.fee === LicensingFeePerRateBreakdownFeeEnum.Sem1FirstStudent
            ? { ...acc.semester1FirstStudentsInfo, ...curr }
            : acc.semester1FirstStudentsInfo,
        semester1AdditionalStudentInfo:
          curr.fee === LicensingFeePerRateBreakdownFeeEnum.Sem1AdditionalStudent
            ? { ...acc.semester1AdditionalStudentInfo, ...curr }
            : acc.semester1AdditionalStudentInfo,
        semester2FirstStudentsInfo:
          curr.fee === LicensingFeePerRateBreakdownFeeEnum.Sem2FirstStudent
            ? { ...acc.semester2FirstStudentsInfo, ...curr }
            : acc.semester2FirstStudentsInfo,
        semester2AdditionalStudentInfo:
          curr.fee === LicensingFeePerRateBreakdownFeeEnum.Sem2AdditionalStudent
            ? { ...acc.semester2AdditionalStudentInfo, ...curr }
            : acc.semester2AdditionalStudentInfo,
        firstSpotInvitation:
          curr.fee === LicensingFeePerRateBreakdownFeeEnum.FirstSpotInvitation
            ? { ...acc.firstSpotInvitation, ...curr }
            : acc.firstSpotInvitation,
        additionalSpotsInvitation:
          curr.fee ===
          LicensingFeePerRateBreakdownFeeEnum.AdditionalSpotsInvitation
            ? { ...acc.additionalSpotsInvitation, ...curr }
            : acc.additionalSpotsInvitation,
      }
    },
    {
      semester1FirstStudentsInfo: breakdownInitialValue,
      semester1AdditionalStudentInfo: breakdownInitialValue,
      semester2FirstStudentsInfo: breakdownInitialValue,
      semester2AdditionalStudentInfo: breakdownInitialValue,
      firstSpotInvitation: breakdownInitialValue,
      additionalSpotsInvitation: breakdownInitialValue,
    }
  )

  const {
    semester1FirstStudentsInfo,
    semester1AdditionalStudentInfo,
    semester2FirstStudentsInfo,
    semester2AdditionalStudentInfo,
    firstSpotInvitation,
    additionalSpotsInvitation,
  } = breakdown

  const licensingDueBreakDownCSSProps = {
    color: theme.palette.primary.main,
    padding: theme.spacing(2, 0),
    minWidth: '88px',
    maxHeight: '30px',
    borderBottom: 1,
    borderColor: theme.palette.customBackground.divider,
    backgroundColor: 'transparent',
    paddingLeft: 0,
  }

  const {
    firstStudent: licensingFeeFirstStudent,
    additionalStudent: licensingFeeAdditionalStudent,
    subTotal: licensingFeeSubtotal,
    totalOfStudents: licensingFeeTotalOfStudents,
  } = useBreakdownDataLicensingFee({
    semesterStudentInfo: firstSpotInvitation,
    semesterAdditionalStudentInfo: additionalSpotsInvitation,
  })

  const {
    firstStudent: semester1FirstStudent,
    additionalStudent: semester1AdditionalStudent,
    subTotal: semester1Subtotal,
    totalOfStudents: semester1TotalOfStudents,
  } = useBreakdownDataLicensingFee({
    semesterStudentInfo: semester1FirstStudentsInfo,
    semesterAdditionalStudentInfo: semester1AdditionalStudentInfo,
  })

  const {
    firstStudent: semester2FirstStudent,
    additionalStudent: semester2AdditionalStudent,
    subTotal: semester2Subtotal,
    totalOfStudents: semester2TotalOfStudents,
  } = useBreakdownDataLicensingFee({
    semesterStudentInfo: semester2FirstStudentsInfo,
    semesterAdditionalStudentInfo: semester2AdditionalStudentInfo,
  })

  const tableHeaders: TableHeaders[] = [
    {
      label: t('LicensingPayment.Breakdown.Header.FeeName', 'Fee Name'),
      align: 'left',
      cssProps: {
        ...licensingDueBreakDownCSSProps,
      },
    },
    {
      label: t('LicensingPayment.Breakdown.Header.FeeAmount', 'Fee Amount'),
      align: 'right',
      cssProps: {
        ...licensingDueBreakDownCSSProps,
      },
      id: 'amount-first-students',
    },
    {
      label: t(
        'LicensingPayment.Breakdown.Header.NumberOfStudents',
        '# of Students'
      ),
      align: 'right',
      cssProps: {
        ...licensingDueBreakDownCSSProps,
      },
    },
    {
      label: t('LicensePayment.Breakdown.Header.Subtotal', 'Subtotal'),
      align: 'right',
      cssProps: {
        ...licensingDueBreakDownCSSProps,
      },
    },
  ]

  const previewHeaderLabels: TableHeaders[] = [
    {
      label: t('BreakdownLedger.Preview.Header.FeeName', 'Fee Name'),
      align: 'left',
      cssProps: {
        ...licensingDueBreakDownCSSProps,
      },
    },
    {
      label: '',
      align: 'left',
      cssProps: {
        ...licensingDueBreakDownCSSProps,
      },
      id: 'amount',
    },
    {
      label: t(
        'BreakdownLedger.Preview.Header.NumberOfStudents',
        '# of Students'
      ),
      align: 'right',
      cssProps: {
        ...licensingDueBreakDownCSSProps,
      },
    },
    {
      label: t('BreakdownLedger.Preview.Header.Subtotal', 'Subtotal'),
      align: 'right',
      cssProps: {
        ...licensingDueBreakDownCSSProps,
      },
    },
  ]

  const previewRows = [
    {
      label: isLcd
        ? lcdLabels.licensingFeeApplication
        : licensingFeeApplicationLabel,
      numberStudents: licensingFeeTotalOfStudents,
      subtotal: licensingFeeSubtotal,
      currencyCode: currencyCode,
    },
    {
      label: isChallenge
        ? isLcd
          ? lcdLabels.firstSemesterLicensingTuition
          : firstSemesterLicensingTuitionLabel
        : isLcd
        ? lcdLabels.licenseFee
        : licensingTuitionLabel,
      numberStudents: semester1TotalOfStudents,
      subtotal: semester1Subtotal,
      currencyCode: currencyCode,
    },
    {
      label: isChallenge
        ? isLcd
          ? lcdLabels.secondSemesterLicensingTuition
          : secondSemesterLicensingTuitionLabel
        : isLcd
        ? lcdLabels.licenseFee
        : licensingTuitionLabel,
      numberStudents: semester2TotalOfStudents,
      subtotal: semester2Subtotal,
      currencyCode: currencyCode,
    },
  ]

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          width: '60%',
          [theme.breakpoints.down('sm')]: {
            width: 'auto',
            height: 700,
          },
        }}
      >
        {showDetails ? (
          <BreakdownLedgerContainer headers={tableHeaders}>
            {convertTwoDecimalStringToBig(licensingFeeSubtotal).gt(0) ? (
              <BreakdownLedger
                type={BreakDownLedgerVariants.Normal}
                headerLabel={
                  isLcd
                    ? lcdLabels.licensingFeeApplication
                    : licensingFeeApplicationLabel
                }
                firstStudent={licensingFeeFirstStudent}
                additionalStudent={licensingFeeAdditionalStudent}
                subtotal={licensingFeeSubtotal}
                isChallenge={isChallenge}
              />
            ) : (
              <></>
            )}
            {convertTwoDecimalStringToBig(semester1Subtotal).gt(0) ? (
              <BreakdownLedger
                type={BreakDownLedgerVariants.Normal}
                headerLabel={
                  isChallenge
                    ? isLcd
                      ? lcdLabels.firstSemesterLicensingTuition
                      : firstSemesterLicensingTuitionLabel
                    : isLcd
                    ? lcdLabels.licenseFee
                    : licensingTuitionLabel
                }
                firstStudent={semester1FirstStudent}
                additionalStudent={semester1AdditionalStudent}
                subtotal={semester1Subtotal}
                isChallenge={isChallenge}
              />
            ) : (
              <></>
            )}
            {isChallenge &&
            convertTwoDecimalStringToBig(semester2Subtotal).gt(0) ? (
              <BreakdownLedger
                type={BreakDownLedgerVariants.Normal}
                headerLabel={
                  isLcd
                    ? lcdLabels.secondSemesterLicensingTuition
                    : secondSemesterLicensingTuitionLabel
                }
                firstStudent={semester2FirstStudent}
                additionalStudent={semester2AdditionalStudent}
                subtotal={semester2Subtotal}
                isChallenge={isChallenge}
              />
            ) : (
              <></>
            )}
            <BreakdownLedger
              type={BreakDownLedgerVariants.Balance}
              totalOwed={owed}
              totalPaid={paid}
              balance={balance}
              currencyCode={currencyCode}
              licensingDue={licensingDue?.amount ?? '0.00'}
            />
          </BreakdownLedgerContainer>
        ) : (
          <BreakdownLedgerContainer headers={previewHeaderLabels}>
            <BreakdownLedger
              type={BreakDownLedgerVariants.Preview}
              rows={previewRows}
              isChallenge={isChallenge}
            />
            <BreakdownLedger
              type={BreakDownLedgerVariants.Balance}
              totalOwed={owed}
              totalPaid={paid}
              balance={balance}
              currencyCode={currencyCode}
              licensingDue={licensingDue?.amount ?? '0.00'}
            />
          </BreakdownLedgerContainer>
        )}
      </Box>
    </Box>
  )
}
